import React from 'react';

const fmt = (n) => '£' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';

export default function MatchCard({ match, onApprove, onExplain, explainLoading, onChangeInvoice, onIgnore, onUndo, approveLoading, isPosted }) {
  const { transaction, invoice, invoices = [], confidence, matchType, reasoning, flags = [], suggestedAccountCode, suggestedContactName, source } = match;

  // Determine if this is a multi-invoice match
  const isMulti = invoices.length > 1;
  // Primary invoice for single-invoice display (backwards compat)
  const primaryInvoice = isMulti ? null : (invoice || invoices[0] || null);

  const isUnmatched = matchType === 'unmatched';
  const isExact = matchType === 'exact';
  const isPartial = matchType === 'partial';
  const isCoded = matchType === 'coded';
  const isIgnored = matchType === 'ignored';

  const borderColor = isPosted ? '#4935A8'
    : isExact ? '#4935A8'
    : isCoded ? '#2E92B6'
    : isIgnored ? '#888'
    : isPartial ? '#BA7517'
    : '#A32D2D';
  const pillColor = isPosted ? { bg: '#EEEBFF', color: '#33247C' }
    : isExact ? { bg: '#EEEBFF', color: '#33247C' }
    : isCoded ? { bg: '#E5F6FC', color: '#16718F' }
    : isIgnored ? { bg: '#EDEDEB', color: '#555' }
    : isPartial ? { bg: '#FAEEDA', color: '#633806' }
    : { bg: '#FCEBEB', color: '#791F1F' };

  const pillLabel = isPosted ? 'Posted to Xero'
    : isCoded ? 'Coded by rule'
    : isIgnored ? 'Ignored by rule'
    : isExact ? (isMulti ? 'Multi-invoice match' : 'Confident match')
    : isPartial ? 'Needs review'
    : 'Unmatched';

  // Total amount of matched invoices
  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + (parseFloat(inv?.amountDue) || 0), 0);

  return (
    <div className="match-card" style={{ borderLeft: `3px solid ${borderColor}` }}>
      {/* Header */}
      <div className="match-card-header">
        <span className="match-pill" style={{ background: pillColor.bg, color: pillColor.color }}>
          {pillLabel}
        </span>
        {confidence > 0 && !isPosted && !isCoded && !isIgnored && (
          <span className="confidence">
            {source === 'ai' ? 'AI confidence' :
             source === 'deterministic' ? 'Rule confidence' :
             source === 'manual' ? 'Manual override' :
             'Match confidence'}: {confidence}%
          </span>
        )}
        {isPosted && <span className="posted-label">✓ Reconciled</span>}
      </div>

      {/* Confidence bar — hide for rule-coded matches (they have no AI confidence) */}
      {confidence > 0 && !isPosted && !isCoded && !isIgnored && (
        <div className="conf-bar-track">
          <div
            className="conf-bar-fill"
            style={{
              width: `${confidence}%`,
              background: confidence >= 85 ? '#4935A8' : confidence >= 60 ? '#BA7517' : '#A32D2D'
            }}
          />
        </div>
      )}

      {/* Transaction vs Invoice(s) */}
      <div className="match-body">
        {/* Bank transaction side */}
        <div className="match-box">
          <div className="match-box-label">Bank transaction · {fmtDate(transaction?.date)}</div>
          <div className="match-box-name">{transaction?.contactName || transaction?.description || '—'}</div>
          <div className="match-box-ref">{transaction?.reference}</div>
          <div className="match-box-amount" style={{ color: '#4935A8' }}>
            {fmt(transaction?.amount || 0)}
          </div>
        </div>

        <div className="match-arrow">→</div>

        {/* Invoice side — single or multi */}
        <div className="match-box" style={{ flex: isMulti ? 2 : 1 }}>
          {isMulti ? (
            // Multi-invoice layout
            <>
              <div className="match-box-label">
                {invoices.length} invoices · {fmt(totalInvoiceAmount)}
              </div>
              <div className="multi-invoice-list">
                {invoices.map((inv, idx) => (
                  <div key={inv.id || idx} className="multi-invoice-row">
                    <span className="multi-invoice-num">{inv.invoiceNumber}</span>
                    <span className="multi-invoice-contact">{inv.contactName}</span>
                    <span className="multi-invoice-date">{fmtDate(inv.date)}</span>
                    <span
                      className="multi-invoice-amount"
                      style={{ color: '#4935A8', fontWeight: 600 }}
                    >
                      {fmt(inv.amountDue || 0)}
                    </span>
                  </div>
                ))}
              </div>
              {Math.abs(transaction?.amount - totalInvoiceAmount) > 0.01 && (
                <div className="match-box-amount" style={{ color: '#BA7517', marginTop: 4, fontSize: 12 }}>
                  Difference: {fmt(Math.abs((transaction?.amount || 0) - totalInvoiceAmount))}
                </div>
              )}
            </>
          ) : primaryInvoice ? (
            // Single-invoice layout
            <>
              <div className="match-box-label">Invoice · {fmtDate(primaryInvoice?.date)}</div>
              <div className="match-box-name">{primaryInvoice?.contactName}</div>
              <div className="match-box-ref">{primaryInvoice?.invoiceNumber}</div>
              <div
                className="match-box-amount"
                style={{ color: Math.abs((transaction?.amount || 0) - (primaryInvoice?.amountDue || 0)) <= 0.01 ? '#4935A8' : '#BA7517' }}
              >
                {fmt(primaryInvoice?.amountDue || 0)}
              </div>
            </>
          ) : (
            // Unmatched / coded / ignored
            <>
              <div className="match-box-label">
                {isCoded ? 'Coded' : isIgnored ? 'Ignored' : 'Invoice'}
              </div>
              {isCoded ? (
                <>
                  <div className="match-box-name">{suggestedContactName || '(no contact)'}</div>
                  {suggestedAccountCode && (
                    <div className="suggested-code">Account {suggestedAccountCode}</div>
                  )}
                </>
              ) : isIgnored ? (
                <div className="match-box-no-match">Ignored — no posting needed</div>
              ) : (
                <>
                  <div className="match-box-no-match">No matching invoice found</div>
                  {suggestedAccountCode && (
                    <div className="suggested-code">Suggested: {suggestedAccountCode}</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* AI reasoning */}
      <div className="match-reasoning">
        <span className="ai-spark">✦</span>
        <span>{reasoning}</span>
      </div>

      {/* Flags */}
      {flags.length > 0 && (
        <div className="match-flags">
          {flags.map(flag => (
            <span key={flag} className="flag-chip">
              {flag.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      {/* Source badge for transparency */}
      {source && source !== 'ai' && !isPosted && (
        <div className="match-source">
          {source === 'rule' ? '⚙ Matched by your coding rule' :
           source === 'deterministic' ? '✓ Matched by rules (no AI)' : ''}
        </div>
      )}

      {/* Actions */}
      {!isPosted && (
        <div className="match-actions">
          {!isUnmatched && !isIgnored && onApprove && (
            <button
              className="btn-approve"
              onClick={onApprove}
              disabled={approveLoading}
            >
              {approveLoading ? 'Posting…' :
                isExact ? (isMulti ? `Post ${invoices.length} invoices to Xero` : 'Post to Xero') : 'Accept & post'}
            </button>
          )}
          {onChangeInvoice && !isIgnored && (
            <button className="btn-explain" onClick={onChangeInvoice}>
              {primaryInvoice || isMulti ? 'Change invoice' : 'Pick invoice'}
            </button>
          )}
          {onIgnore && !isIgnored && (
            <button className="btn-explain" onClick={onIgnore}>
              Ignore
            </button>
          )}
          {onUndo && (
            <button className="btn-explain" onClick={onUndo}>
              Undo
            </button>
          )}
          {onExplain && (
            <button
              className="btn-explain"
              onClick={onExplain}
              disabled={explainLoading}
            >
              {explainLoading ? 'Asking AI...' : isUnmatched ? 'Suggest coding ↗' : 'Explain match ↗'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
